/**
 * CREATIVE DETONATOR - Phase 3: Detonation
 * AI idea generation via backend API with Mac-style progress UI
 *
 * Features:
 * - Mac-style dialog with 4-phase progress tracking
 * - Animated progress bar with phase-specific colors
 * - Real-time streaming via SSE (Server-Sent Events)
 * - V1/V2/V3 mode support
 * - Session data storage for export
 * - Error handling with visual feedback
 */

// v65 - Added V5 brand integration (pass brandId to API)
import { GeneratorState, API_CONFIG, setSession, resetV2Progress, loadImageConfig, resetPreloadedImages, getBrandId, persistSessionState, clearSessionState } from '../core/state.js';
import { goToPhase } from '../core/navigation.js';
import { autoGenerateAllAndWait, supportsImageGeneration, saveImageToDatabase } from '../features/image-generation.js';
import { getSessionToken } from '../../auth/js/client.js';
import { populateDummyData } from '../core/dummy-data.js';
import { showError } from '../../../js/core/error-handler.js';
import { createLogger } from '../../../js/core/logger.js';
import { animateCoinSpend, updateCreditDisplays } from '../../../js/shared/credit-animation.js';

const log = createLogger('Detonation');

// Credit cost for text generation (should match backend pricing)
const CREDIT_COST_TEXT_GENERATION = 10;

/**
 * Refresh credit balance from server and update all displays
 */
async function refreshCreditsFromServer() {
  try {
    const token = await getSessionToken();
    if (!token) return;

    const response = await fetch(`${API_CONFIG.baseUrl}/api/credits`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && typeof data.credits === 'number') {
        updateCreditDisplays(data.credits);
        log.debug('Credits refreshed:', data.credits);
      }
    }
  } catch (err) {
    log.warn('Failed to refresh credits:', err.message);
  }
}

// Access globals from window scope
const DataStore = window.DataStore;
const DetonationUI = window.DetonationUI;

// ============================================================================
// SAFE UI WRAPPER - prevents any UI error from crashing the stream
// ============================================================================

function safeUI(fn, fallbackMsg = 'UI update') {
  try {
    if (typeof fn === 'function') fn();
  } catch (e) {
    log.warn(`SafeUI: ${fallbackMsg} failed:`, e.message);
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getFormatNames(formats) {
  const formatMap = {
    'social': 'SOCIAL',
    'video': 'VIDEO',
    'print': 'PRINT',
    'experiential': 'EXPERIENTIAL',
    'digital': 'DIGITAL',
    'campaign': 'CAMPAIGN'
  };
  return formats.map(f => {
    if (typeof f === 'string') return formatMap[f] || f.toUpperCase();
    return f.name || f.toUpperCase();
  });
}

function getTriggerCodes(triggers) {
  return triggers.map(t => {
    if (typeof t === 'string') return t;
    return t.code || t;
  });
}

// ============================================================================
// PROGRESS SIMULATION (Legacy fallback for non-streaming API)
// V3 5-Phase Flow: Fragment → Interpret → Select → Execute → Images
// ============================================================================

async function simulateProgressDuringApiCall(withImages) {
  const endProgress = withImages ? 70 : 95;

  // Phase 1: FRAGMENT (0-20%)
  DetonationUI.startPhase('1');
  for (let i = 0; i <= 20; i += 2) {
    DetonationUI.updateProgress(i);
    await sleep(120);
  }

  // Phase 2a: INTERPRET (20-35%)
  DetonationUI.startPhase('2a');
  for (let i = 20; i <= 35; i += 2) {
    DetonationUI.updateProgress(i);
    await sleep(100);
  }

  // Phase 2b: SELECT (35-50%)
  DetonationUI.startPhase('2b');
  for (let i = 35; i <= 50; i += 2) {
    DetonationUI.updateProgress(i);
    await sleep(100);
  }

  // Phase 3: EXECUTE (50-70% or 50-95%)
  DetonationUI.startPhase('3');
  for (let i = 50; i <= endProgress; i += 2) {
    DetonationUI.updateProgress(i);
    await sleep(80);
  }
}

// ============================================================================
// MAIN DETONATION FUNCTION
// ============================================================================

export async function runDetonation() {
  GeneratorState.isGenerating = true;
  GeneratorState.generatedIdeas = [];
  resetV2Progress();
  resetPreloadedImages();
  clearSessionState();  // Clear old session state before new generation

  // ========================================================================
  // SIMULATION MODE CHECK (Dev/Admin Tool)
  // If simulationMode is true, run fake progress instead of real API calls
  // ========================================================================
  if (GeneratorState.simulationMode) {
    log.debug(' ⚡ SIMULATION MODE ACTIVE - No API calls will be made');
    return runDetonationSimulation();
  }

  log.debug(' Loading config...');
  await loadImageConfig();
  log.debug(' Config loaded:', {
    streamingEnabled: GeneratorState.streamingEnabled,
    autoGeneration: GeneratorState.imageConfig?.autoGeneration,
    autoFormats: GeneratorState.imageConfig?.autoFormats,
    autoProvider: GeneratorState.imageConfig?.autoProvider
  });

  // Use streaming if enabled
  if (GeneratorState.streamingEnabled) {
    log.debug(' >>> USING STREAMING API <<<');
    return runDetonationStreaming();
  }

  // Legacy mode (simulated progress)
  log.debug(' Using legacy API (simulated progress)');
  return runDetonationLegacy();
}

// ============================================================================
// SIMULATION DETONATION (No API Calls)
// Used by dev mode to preview Phase 3 UI without consuming credits
// ============================================================================

async function runDetonationSimulation() {
  const { brief, selectedTriggers } = GeneratorState;

  // Simulate loading config (use defaults or current config)
  await loadImageConfig().catch(() => {
    log.debug('Simulation: Config load skipped (no backend)');
  });

  const imageConfig = GeneratorState.imageConfig || {};
  const withImages = imageConfig.autoGeneration || false;

  log.debug('Simulation: Starting simulation sequence');
  log.debug('Simulation: Brief:', brief.product);
  log.debug('Simulation: Triggers:', selectedTriggers);
  log.debug('Simulation: With Images:', withImages);

  // Initialize UI
  safeUI(() => {
    DetonationUI.init({
      product: brief.product || 'Simulation Product',
      benefit: brief.benefit || 'Testing benefit',
      formats: getFormatNames(brief.formats || ['print', 'video']),
      triggers: getTriggerCodes(selectedTriggers || ['A1', 'B2', 'C1']),
      withImages: withImages
    });
  }, 'DetonationUI.init');

  safeUI(() => DetonationUI.startTimer(), 'DetonationUI.startTimer');

  // Helper for delays
  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // ========================================================================
  // Phase 1: FRAGMENT (0-20%)
  // ========================================================================
  log.debug('Simulation: Phase 1: FRAGMENT');
  safeUI(() => DetonationUI.startPhase('1'), "startPhase('1')");

  for (let i = 0; i <= 20; i += 2) {
    safeUI(() => DetonationUI.updateProgress(i), `updateProgress ${i}`);
    await wait(80);
  }
  safeUI(() => DetonationUI.setResult('fragments', 48), 'setResult fragments');

  // ========================================================================
  // Phase 2a: INTERPRET (20-35%)
  // ========================================================================
  log.debug('Simulation: Phase 2a: INTERPRET');
  safeUI(() => DetonationUI.startPhase('2a'), "startPhase('2a')");

  for (let i = 20; i <= 35; i += 2) {
    safeUI(() => DetonationUI.updateProgress(i), `updateProgress ${i}`);
    await wait(80);
  }
  safeUI(() => DetonationUI.setResult('interpreted', 20), 'setResult interpreted');

  // ========================================================================
  // Phase 2b: SELECT (35-50%)
  // ========================================================================
  log.debug('Simulation: Phase 2b: SELECT');
  safeUI(() => DetonationUI.startPhase('2b'), "startPhase('2b')");

  for (let i = 35; i <= 50; i += 2) {
    safeUI(() => DetonationUI.updateProgress(i), `updateProgress ${i}`);
    await wait(80);
  }
  safeUI(() => DetonationUI.setResult('selected', 8), 'setResult selected');

  // ========================================================================
  // Phase 3: EXECUTE (50-70% or 50-95%)
  // ========================================================================
  log.debug('Simulation: Phase 3: EXECUTE');
  safeUI(() => DetonationUI.startPhase('3'), "startPhase('3')");

  // Populate dummy data NOW (before showing results)
  populateDummyData();

  const totalIdeas = GeneratorState.generatedIdeas.reduce((sum, g) => sum + g.ideas.length, 0);
  const endProgress = withImages ? 70 : 95;

  for (let i = 50; i <= endProgress; i += 2) {
    safeUI(() => DetonationUI.updateProgress(i), `updateProgress ${i}`);
    await wait(60);
  }
  safeUI(() => DetonationUI.setResult('ideas', totalIdeas), 'setResult ideas');

  // ========================================================================
  // Phase 4: IMAGES (70-100%) - Only if auto-generation enabled
  // ========================================================================
  if (withImages) {
    log.debug('Simulation: Phase 4: IMAGES');
    safeUI(() => DetonationUI.startPhase('4'), "startPhase('4')");

    // Simulate image generation (no actual API calls)
    const totalImages = Math.min(totalIdeas, 6);  // Cap for simulation

    for (let img = 1; img <= totalImages; img++) {
      const progress = 70 + (img / totalImages * 25);
      safeUI(() => DetonationUI.updateProgress(progress), `updateProgress ${progress}`);
      safeUI(() => DetonationUI.setResult('images', `${img}/${totalImages}`), 'setResult images');
      safeUI(() => DetonationUI.setMessage(`[SIM] Generating image ${img}/${totalImages}...`), 'setMessage');
      await wait(200);  // Fast simulation
    }

    safeUI(() => DetonationUI.setResult('images', totalImages), 'setResult images final');
    safeUI(() => DetonationUI.setMessage('[SIM] All images simulated!'), 'setMessage complete');
  }

  // ========================================================================
  // Complete
  // ========================================================================
  safeUI(() => DetonationUI.updateProgress(100), 'updateProgress 100');
  safeUI(() => DetonationUI.complete(), 'complete');

  log.debug('Simulation: Complete! Transitioning to Phase 4...');

  await wait(1500);

  // Reset simulation flag so next real detonation works normally
  GeneratorState.simulationMode = false;

  goToPhase(4);
  GeneratorState.isGenerating = false;
}

// ============================================================================
// LEGACY DETONATION (Non-streaming)
// ============================================================================

async function runDetonationLegacy() {
  const { brief, selectedTriggers, imageConfig } = GeneratorState;
  const withImages = imageConfig.autoGeneration;

  DetonationUI.init({
    product: brief.product,
    benefit: brief.benefit,
    formats: getFormatNames(brief.formats),
    triggers: getTriggerCodes(selectedTriggers),
    withImages: withImages
  });

  DetonationUI.startTimer();
  DetonationUI.startPhase(1);

  try {
    // --- ROBUST TOKEN RETRIEVAL ---
    let token = null;
    try {
      token = await getSessionToken();
      if (!token) {
        log.debug(' getSessionToken returned null');
      }
    } catch (e) {
      log.error(' Error retrieving token:', e);
    }

    if (!token) {
      log.error(' FATAL: No auth token available. Aborting request.');
      throw new Error('Authentication token missing. Please refresh and log in again.');
    }

    log.debug(' Token retrieved successfully. Length:', token.length);
    // --- END TOKEN RETRIEVAL ---

    // Build headers
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    // Start API call + progress simulation
    const apiPromise = fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.generate}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        brief: brief,
        triggers: selectedTriggers,
        ideasCount: GeneratorState.ideasCount || 3,
        brandId: getBrandId()  // V5: Include brand ID if brand is loaded
      })
    });

    const simulationPromise = simulateProgressDuringApiCall(withImages);
    const response = await apiPromise;
    await simulationPromise;

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Generation failed');
    }

    // CREDIT-FIX: Show coin spend animation AFTER successful response
    // This prevents animation from playing if insufficient credits (402 error)
    animateCoinSpend(CREDIT_COST_TEXT_GENERATION);

    // Update UI with real values (V3 5-phase)
    if (data.meta?.phases) {
      DetonationUI.setResult('fragments', data.meta.phases.fragmentsGenerated || '—');
      // V3 has both interpreted and selected counts
      if (data.meta.phases.fragmentsInterpreted) {
        DetonationUI.setResult('interpreted', data.meta.phases.fragmentsInterpreted);
      }
      DetonationUI.setResult('selected', data.meta.phases.fragmentsSelected || '—');
    }

    const ideasCount = data.meta?.ideas_generated ||
      data.data?.reduce((sum, group) => sum + (group.ideas?.length || 0), 0) || 0;
    DetonationUI.setResult('ideas', ideasCount);

    if (data.session) {
      setSession(data.session);
    }

    // Process results
    processGenerationResults(data);

    // Image generation
    if (withImages) {
      await handleImageGeneration();
    }

    DetonationUI.updateProgress(100);
    DetonationUI.complete();

    // Refresh credits from server (animation already shown on submit)
    refreshCreditsFromServer();

    await sleep(1500);
    goToPhase(4);

  } catch (error) {
    log.error(' Error:', error);
    DetonationUI.error(error.message);
    showError(error, { context: 'generating ideas', modal: true });
    setTimeout(() => {
      DetonationUI.reset();
      goToPhase(2);
    }, 3000);
  } finally {
    GeneratorState.isGenerating = false;
  }
}

// ============================================================================
// STREAMING DETONATION (Fetch API with ReadableStream)
// ============================================================================

async function runDetonationStreaming() {
  log.debug(' ====== STARTING STREAMING ======');

  try {
    log.debug(' Step 1: Getting state...');
    const { brief, selectedTriggers, imageConfig } = GeneratorState;
    const withImages = imageConfig.autoGeneration;

    // Very visible log showing config state at stream start
    console.log('[STREAMING] Starting with config:', {
      withImages,
      autoGeneration: imageConfig.autoGeneration,
      autoFormats: imageConfig.autoFormats,
      product: brief.product
    });

    log.debug(' Step 1 OK - brief:', brief.product, 'withImages:', withImages);

    // Initialize UI - wrapped in safeUI so it never crashes
    log.debug(' Step 2: Initializing UI...');
    safeUI(() => {
      DetonationUI.init({
        product: brief.product,
        benefit: brief.benefit,
        formats: getFormatNames(brief.formats),
        triggers: getTriggerCodes(selectedTriggers),
        withImages: withImages
      });
    }, 'DetonationUI.init');
    log.debug(' Step 2 OK - UI initialized');

    log.debug(' Step 3: Starting timer...');
    safeUI(() => DetonationUI.startTimer(), 'DetonationUI.startTimer');
    log.debug(' Step 3 OK - Timer started');

    const streamState = {
      currentPhase: 0,
      result: null,
      eventsReceived: 0,
      // SSE parsing state (for line-by-line parser)
      currentEventType: null,
      currentData: ''
    };

    const streamUrl = `${API_CONFIG.baseUrl}/api/generate-stream`;
    log.debug(' Step 4: Preparing request to:', streamUrl);

    // --- ROBUST TOKEN RETRIEVAL ---
    let token = null;
    try {
      token = await getSessionToken();
      if (!token) {
        log.debug(' getSessionToken returned null');
      }
    } catch (e) {
      log.error(' Error retrieving token:', e);
    }

    if (!token) {
      log.error(' FATAL: No auth token available. Aborting request.');
      throw new Error('Authentication token missing. Please refresh and log in again.');
    }

    log.debug(' Token retrieved successfully. Length:', token.length);
    // Cache token for later use (avoids deadlock when saving images)
    GeneratorState.cachedAuthToken = token;
    // --- END TOKEN RETRIEVAL ---

    // Build headers
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Authorization': `Bearer ${token}`
    };

    // Use Fetch API with ReadableStream
    // Note: Do NOT use keepalive - it's for page unload beacons and has 64KB limit
    const currentBrandId = getBrandId();
    log.debug(' Brand ID for generation:', currentBrandId, 'currentBrand:', GeneratorState.currentBrand?.name || 'NONE');

    const response = await fetch(streamUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        brief: brief,
        triggers: selectedTriggers,
        ideasCount: GeneratorState.ideasCount || 3,
        brandId: currentBrandId  // V5: Include brand ID if brand is loaded
      })
    });

    log.debug(' Step 5: Response received, status:', response.status);

    if (!response.ok) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }

    // CREDIT-FIX: Show coin spend animation AFTER successful response
    // This prevents animation from playing if insufficient credits (402 error)
    animateCoinSpend(CREDIT_COST_TEXT_GENERATION);

    // Read the stream
    log.debug(' Step 6: Getting reader...');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    log.debug(' Step 6 OK - Reader obtained');

    log.debug(' Step 7: Starting read loop...');

    let readCount = 0;
    while (true) {
      readCount++;
      log.debug(` Read #${readCount}: calling reader.read()...`);

      const { done, value } = await reader.read();

      if (done) {
        log.debug(' Stream done after', readCount, 'reads,', streamState.eventsReceived, 'events');
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      log.debug(` Read #${readCount}: received ${chunk.length} bytes`);
      buffer += chunk;

      // Line-by-line SSE parsing (more robust than split on \n\n)
      // This handles multi-line data fields and edge cases properly
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line

      for (const line of lines) {
        // Skip heartbeat comments (lines starting with :)
        if (line.startsWith(':')) continue;

        // Parse event type
        if (line.startsWith('event:')) {
          streamState.currentEventType = line.substring(6).trim();
        }
        // Accumulate data (supports multi-line data fields per SSE spec)
        else if (line.startsWith('data:')) {
          streamState.currentData = (streamState.currentData || '') + line.substring(5).trim();
        }
        // Empty line = end of event, process accumulated data
        else if (line === '') {
          if (streamState.currentEventType && streamState.currentData) {
            const eventType = streamState.currentEventType;
            const eventData = streamState.currentData;

            streamState.eventsReceived++;
            log.debug(`SSE: Event #${streamState.eventsReceived}: ${eventType}`);

            try {
              const data = JSON.parse(eventData);

              if (eventType === 'connected') {
                log.debug('SSE: ✓ Stream connected');
              } else if (eventType === 'phase') {
                log.debug('SSE: Calling handlePhaseEvent...');
                handlePhaseEvent(data, withImages, streamState);
                log.debug('SSE: handlePhaseEvent done');
              } else if (eventType === 'progress') {
                handleProgressEvent(data, streamState);
              } else if (eventType === 'complete') {
                log.debug('SSE: ★ COMPLETE received');
                streamState.result = data;
              } else if (eventType === 'error') {
                log.error('SSE: Error event:', data);
                throw new Error(data.message || 'Server reported error');
              }
            } catch (e) {
              log.warn('SSE: Parse error:', e.message, 'Data:', eventData.substring(0, 100));
              if (eventType === 'error') throw e;
            }
          }
          // Reset for next event
          streamState.currentEventType = null;
          streamState.currentData = '';
        }
      }
    }

    // Process results
    log.debug('Processing results...');

    if (streamState.result && streamState.result.success) {
      processGenerationResults(streamState.result);
      log.info(`Ideas loaded: ${GeneratorState.generatedIdeas.length} groups`);

      if (withImages) {
        log.info('Starting auto-image generation...');

        // CIRCUIT BREAKER: 3 minute timeout prevents infinite hang
        // If images fail/timeout, user still gets their text ideas
        const IMAGE_PHASE_TIMEOUT = 180000; // 3 minutes

        try {
          await Promise.race([
            handleImageGeneration(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Image generation phase timed out')), IMAGE_PHASE_TIMEOUT)
            )
          ]);
          log.info('Image generation complete');
        } catch (imageError) {
          log.warn('Image generation issue:', imageError.message);
          safeUI(() => DetonationUI.setMessage('Continuing with available images...'), 'setMessage timeout');
          // Don't fail - continue to Phase 4 with whatever we have
        }
      } else {
        log.debug('Skipping auto-images (disabled in config)');
      }

      safeUI(() => DetonationUI.updateProgress(100), 'updateProgress 100');
      safeUI(() => DetonationUI.complete(), 'complete');

      // Refresh credits from server (animation already shown on submit)
      refreshCreditsFromServer();

      // Persist state to sessionStorage for page refresh recovery
      persistSessionState();

      await sleep(1500);
      goToPhase(4);
    } else {
      log.error(' No complete event received after', streamState.eventsReceived, 'events');
      throw new Error('Stream ended without complete result. Please try again.');
    }

  } catch (error) {
    log.error(' FATAL:', error);
    safeUI(() => DetonationUI.error(error.message), 'error');

    showError(error, {
      context: 'streaming generation',
      modal: true,
      onRetry: () => {
        safeUI(() => DetonationUI.reset(), 'reset');
        goToPhase(2);
      }
    });

  } finally {
    GeneratorState.isGenerating = false;
  }
}

// ============================================================================
// EVENT HANDLERS
// V3 Backend → UI Phase Mapping:
//   Backend phase 1 → UI card '1' (FRAGMENT)
//   Backend phase 2 started → UI card '2a' (INTERPRET)
//   Backend progress interpreting → UI card '2a' value
//   Backend progress scoring → UI card '2b' (SELECT)
//   Backend phase 2 complete → UI cards '2a' and '2b' complete
//   Backend phase 3 → UI card '3' (EXECUTE)
//   Frontend phase 4 → UI card '4' (IMAGES)
// ============================================================================

function handlePhaseEvent(data, withImages, streamState) {
  const { phase, status } = data;
  streamState.currentPhase = phase;

  if (status === 'started') {
    // Map backend phases to UI phases
    let uiPhase;
    let uiProgress;

    switch (phase) {
      case 1:
        uiPhase = '1';     // FRAGMENT
        uiProgress = 0;
        break;
      case 2:
        uiPhase = '2a';    // INTERPRET (phase 2 starts with interpret)
        uiProgress = 20;
        break;
      case 3:
        uiPhase = '3';     // EXECUTE
        uiProgress = 50;
        break;
      default:
        uiPhase = String(phase);
        uiProgress = 0;
    }

    log.debug(`UI: ▶ Backend phase ${phase} → UI phase '${uiPhase}' started`);
    safeUI(() => DetonationUI.startPhase(uiPhase), `startPhase('${uiPhase}')`);
    // Don't call setMessage here - startPhase already starts the message cycling
    safeUI(() => DetonationUI.updateProgress(uiProgress), `updateProgress ${uiProgress}`);
  }
  else if (status === 'complete') {
    log.debug(`UI: ✓ Backend phase ${phase} complete`, data);

    switch (phase) {
      case 1:
        // FRAGMENT complete
        log.debug(`UI: Setting fragments: ${data.fragmentsGenerated}`);
        safeUI(() => DetonationUI.setResult('fragments', data.fragmentsGenerated || 0), 'setResult fragments');
        safeUI(() => DetonationUI.updateProgress(20), 'updateProgress 20');
        break;

      case 2:
        // INTERPRET and SELECT both complete
        // V3 sends both fragmentsInterpreted and fragmentsSelected
        if (data.fragmentsInterpreted) {
          log.debug(`UI: Setting interpreted: ${data.fragmentsInterpreted}`);
          safeUI(() => DetonationUI.setResult('interpreted', data.fragmentsInterpreted), 'setResult interpreted');
        }
        log.debug(`UI: Setting selected: ${data.fragmentsSelected}`);
        safeUI(() => DetonationUI.setResult('selected', data.fragmentsSelected || 0), 'setResult selected');
        safeUI(() => DetonationUI.updateProgress(50), 'updateProgress 50');
        break;

      case 3:
        // EXECUTE complete
        log.debug(`UI: Setting ideas: ${data.ideasGenerated}`);
        safeUI(() => DetonationUI.setResult('ideas', data.ideasGenerated || 0), 'setResult ideas');
        safeUI(() => DetonationUI.updateProgress(withImages ? 70 : 95), 'updateProgress 70/95');
        break;
    }
  }
}

function handleProgressEvent(data, streamState) {
  if (data.fragmentsInterpreted) {
    // V3 Phase 2a complete - switch to scoring UI
    log.debug(`UI: Interpreted: ${data.fragmentsInterpreted} insights`);
    safeUI(() => DetonationUI.setResult('interpreted', data.fragmentsInterpreted), 'setResult interpreted');
    safeUI(() => DetonationUI.setMessage(`${data.fragmentsInterpreted} insights extracted`), 'setMessage interpreted');
    safeUI(() => DetonationUI.updateProgress(35), 'updateProgress 35');
  }
  else if (data.status === 'scoring') {
    // V3 Phase 2b starts - switch to SELECT card
    log.debug(`UI: Starting SELECT phase (scoring)`);
    safeUI(() => DetonationUI.startPhase('2b'), "startPhase('2b')");
    // Don't call setMessage - startPhase already starts the message cycling for phase2b
    safeUI(() => DetonationUI.updateProgress(43), 'updateProgress 43');
  }
  else if (data.currentFormat) {
    safeUI(() => DetonationUI.setMessage(`Creating ${data.currentFormat.toUpperCase()} concepts...`), 'setMessage format');
  }
  // Don't handle generic data.message here - let the message cycling continue
}

// ============================================================================
// RESULT PROCESSING
// ============================================================================

function processGenerationResults(result) {
  if (!result.data) return;

  // Store the ID mapping from backend (frontend ID -> database UUID)
  // CRITICAL: Persist to sessionStorage so video generation can access it after page refresh
  if (result.idMapping) {
    // Merge with existing mapping (don't replace - preserves previous session mappings)
    GeneratorState.idMapping = {
      ...GeneratorState.idMapping,
      ...result.idMapping
    };
    log.debug(' Stored ID mapping for', Object.keys(result.idMapping).length, 'ideas');

    // Persist to sessionStorage for video generation module
    try {
      sessionStorage.setItem('cd_id_mapping', JSON.stringify(GeneratorState.idMapping));
      log.debug(' idMapping persisted to sessionStorage');
    } catch (e) {
      log.warn(' Could not persist idMapping:', e);
    }

    // Process any pending video syncs now that we have mappings
    // Import dynamically to avoid circular dependency
    import('../features/video-generation.js').then(module => {
      if (module.processPendingVideoSyncs) {
        module.processPendingVideoSyncs();
      }
    }).catch(() => {});
  }

  result.data.forEach(concept => {
    const fullTrigger = DataStore.getTriggerByCode(concept.triggerCode);

    if (fullTrigger) {
      GeneratorState.generatedIdeas.push({
        trigger: fullTrigger,
        ideas: concept.ideas.map((idea, idx) => ({
          id: idea.id || `${concept.triggerCode}-${Date.now()}-${idx}`,
          title: idea.title,
          headline: idea.headline || '',
          idea: idea.idea || '',
          subline: idea.subline || '',
          insight: idea.insight || idea.idea || '',
          text: idea.visual || idea.text || idea.description || '',
          whyItWorks: idea.whyItWorks || idea.mechanism || '',
          mechanism: idea.whyItWorks || idea.mechanism || '',
          formatNotes: idea.formatNotes || '',
          format: idea.format || GeneratorState.brief.formats[0] || 'print',
          sectionLabel: idea.sectionLabel || 'VISUAL',
          headlineLabel: idea.headlineLabel || 'HEADLINE',
          sourceFragment: idea.sourceFragment || null,
          sourceTruth: idea.sourceTruth || null,
          alternativeHeadlines: idea.alternativeHeadlines || [],
          currentHeadlineIndex: idea.currentHeadlineIndex || 0,
          // Video-specific structured data (for storyboard generation)
          structure: idea.structure || null,
          sceneCount: idea.structure?.scenes?.length || 0,
          // Store brief context for later use (Dashboard mode, modals, etc.)
          meta: {
            brief: { ...GeneratorState.brief },
            brand: GeneratorState.currentBrand || null
          }
        }))
      });
    }
  });

  if (result.session) {
    setSession(result.session);
  }

  const version = result.meta?.version || 'v1';
  const ideasCount = result.meta?.ideas_generated ||
    result.data?.reduce((sum, group) => sum + (group.ideas?.length || 0), 0) || 0;
  log.debug(` ${version}: ${ideasCount} ideas generated`);
}

// ============================================================================
// IMAGE GENERATION
// ============================================================================

async function handleImageGeneration() {
  const imageConfig = GeneratorState.imageConfig || {};

  safeUI(() => DetonationUI.startPhase('4'), "startPhase '4'");
  safeUI(() => DetonationUI.updateProgress(75), 'updateProgress 75');

  let visualCount = 0;
  GeneratorState.generatedIdeas.forEach(group => {
    group.ideas.forEach(idea => {
      if (supportsImageGeneration(idea.format)) visualCount++;
    });
  });

  if (visualCount > 0) {
    safeUI(() => DetonationUI.setMessage(`Generating ${visualCount} images...`), 'setMessage images');
    safeUI(() => DetonationUI.setResult('images', `0/${visualCount}`), 'setResult images init');

    const imageMap = await autoGenerateAllAndWait(
      {
        provider: imageConfig.autoProvider,
        aspectRatio: imageConfig.autoAspect,
        maxConcurrent: imageConfig.maxConcurrent,
        style: imageConfig.autoStyle,
        maxRetries: imageConfig.maxRetries || 2,
        authToken: GeneratorState.cachedAuthToken  // Use cached token to avoid timeout
      },
      (status, progress) => {
        // Only show X/Y progress for COMPLETION status (contains "ready" or "complete")
        // NOT for submission status (contains "Submitted")
        if (status.includes('ready') || status.includes('complete')) {
          const match = status.match(/(\d+)\/(\d+)/);
          if (match) {
            safeUI(() => DetonationUI.setResult('images', `${match[1]}/${match[2]}`), 'setResult images progress');
          }
        }
        safeUI(() => DetonationUI.setMessage(status), 'setMessage status');
        safeUI(() => DetonationUI.updateProgress(progress), 'updateProgress');
      }
    );

    // Store images in state and save to database
    const idMapping = GeneratorState.idMapping || {};
    log.debug(' Saving images to DB. idMapping keys:', Object.keys(idMapping).length);

    // Use cached token from generation start (avoids Supabase auth deadlock)
    // See docs/_TASKS/52-image-save-deadlock.md for details
    const authToken = GeneratorState.cachedAuthToken;
    log.debug(' Using cached auth token:', authToken ? 'OK' : 'MISSING');
    if (!authToken) {
      log.warn(' No cached auth token - skipping all DB saves');
    }

    const savePromises = [];
    imageMap.forEach((url, ideaId) => {
      GeneratorState.preloadedImages[ideaId] = url;
      // Look up database UUID from ID mapping
      const dbId = idMapping[ideaId];
      log.debug(` Image for ${ideaId} -> DB ID: ${dbId || 'NOT FOUND'}`);
      if (dbId && authToken) {
        // Save to database using the real database UUID
        const savePromise = saveImageToDatabase(dbId, url, {
          provider: imageConfig.autoProvider || 'seedream',
          aspectRatio: imageConfig.autoAspect || '4:3'
        }, { token: authToken }).catch(err => {
          log.error(`Failed to save image for ${dbId}:`, err);
        });
        savePromises.push(savePromise);
      } else if (!dbId) {
        log.warn(' No DB ID mapping for idea:', ideaId);
      }
    });

    // Wait for all DB saves to complete (with detailed logging)
    if (savePromises.length > 0) {
      log.debug(` Waiting for ${savePromises.length} DB saves (with retry logic)...`);
      const results = await Promise.allSettled(savePromises);

      // Log detailed results
      const succeeded = results.filter(r => r.status === 'fulfilled' && r.value?.success).length;
      const failed = results.filter(r => r.status === 'rejected' || !r.value?.success).length;
      log.debug(` DB saves complete: ${succeeded} succeeded, ${failed} failed`);

      if (failed > 0) {
        results.forEach((r, i) => {
          if (r.status === 'rejected' || !r.value?.success) {
            const error = r.reason || r.value?.error || 'Unknown';
            log.error(`DB save #${i + 1} failed:`, error);
          }
        });
      }
    }

    const successCount = imageMap.size;
    const failCount = visualCount - successCount;

    log.debug(` Images: ${successCount} success, ${failCount} failed`);

    if (failCount > 0) {
      safeUI(() => DetonationUI.setMessage(`${successCount} images ready, ${failCount} failed`), 'setMessage results');
    } else {
      safeUI(() => DetonationUI.setResult('images', successCount), 'setResult images');
      safeUI(() => DetonationUI.setMessage(`All ${successCount} images ready!`), 'setMessage complete');
    }
  } else {
    safeUI(() => DetonationUI.setResult('images', '0'), 'setResult images 0');
  }
}

// ============================================================================
// FALLBACK GENERATION (for testing without backend)
// ============================================================================

export function generateIdeasForTrigger(trigger) {
  const { product, benefit, format } = GeneratorState.brief;

  return [
    {
      id: `${trigger.code}-${Date.now()}-1`,
      title: `${trigger.name} Concept`,
      headline: '',
      text: `Apply "${trigger.question.replace(/"/g, '')}" to ${product}.`,
      mechanism: trigger.mechanism
    },
    {
      id: `${trigger.code}-${Date.now()}-2`,
      title: `Alternative Approach`,
      headline: '',
      text: `Using ${trigger.name.toLowerCase()}: Show ${product}'s ${benefit} through an unexpected visual.`,
      mechanism: `Exploits ${trigger.name.toLowerCase()} psychology.`
    }
  ];
}

// ============================================================================
// GLOBAL EXPORTS
// ============================================================================

window.runDetonation = runDetonation;
window.generateIdeasForTrigger = generateIdeasForTrigger;
